import * as React from 'react';
import { pathOr } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import { useApplicationContext } from '../../utils/applicationContext';
import { TThunkDispatch, IApplicationState } from '../../types/redux';
import {
  TSuggestResult,
  IYandexSuggestOptions,
  suggest,
  selectGeo,
  clearGeo,
  geocode,
  setRegion,
  setVillage,
  setBSCenters,
  setBuilder,
  setCoworkingIds,
  changeGeoSearch,
  validateGeo,
} from '../../actions/filters';
import { FilterGeo } from '../../components/FilterGeo';
import { getGeoBbox } from '../../actions/filters/geo/getGeoBoundedBy';
import { selectGeoSearch } from '../../selectors/filters/geoSearch';
import { selectIsGeoLoading } from '../../selectors/filters';
import { selectFiltersClientErrors } from '../../selectors/filters/clientErrors';
import { getGeoPlaceholder } from '../../utils/geo';
import { dealTypeFromJsonQuery, offerTypeFromJsonQuery } from '../../utils/category';
import { TGeoValue } from '../../../packages/api-models/common/json_query';
import { selectPageKind } from '../../selectors/pageKind';
import { selectJsonQuery } from '../../selectors/filters/selectJsonQuery';
import { selectUserGeoId } from '../../selectors/userGeo';
import { setCookie } from '@cian/utils/lib/browser/cookie';
import { LOCATION_DETECT_DISABLED_COOKIE_KEY } from '../../constants/geoSwitcher';

export const FilterGeoContainer: React.FC = () => {
  const dispatch = useDispatch<TThunkDispatch>();
  const { telemetry, config } = useApplicationContext();

  const pageKind = useSelector(selectPageKind);
  const jsonQuery = useSelector(selectJsonQuery);
  const suggestions = useSelector((state: IApplicationState) => state.filters.suggestions);
  const userGeoId = useSelector(selectUserGeoId);
  const regionOrMaintownId = pathOr(userGeoId, ['region', 'value'], jsonQuery);
  const search = useSelector(selectGeoSearch);
  const isLoading = useSelector(selectIsGeoLoading);
  const geoError = useSelector((state: IApplicationState) => selectFiltersClientErrors(state).geo);
  const placeholder = useSelector((state: IApplicationState) => {
    const dealType = dealTypeFromJsonQuery(state.filters.jsonQuery);
    const offerType = offerTypeFromJsonQuery(state.filters.jsonQuery);

    return getGeoPlaceholder({ dealType, offerType });
  });

  const locationDetectDisabledCookieExpirePeriod = config.get<number>('LocationDetectDisabledPeriod');

  const getSuggestions = React.useCallback(
    (query: string, options?: IYandexSuggestOptions): Promise<TSuggestResult> => {
      return dispatch(suggest({ query, options }));
    },
    [dispatch],
  );

  const onSetRegion = React.useCallback(
    (region: number[]) => {
      if (locationDetectDisabledCookieExpirePeriod) {
        setCookie(LOCATION_DETECT_DISABLED_COOKIE_KEY, '1', {
          expires: locationDetectDisabledCookieExpirePeriod,
          secure: false,
        });
      }

      dispatch(setRegion(region));
    },
    [dispatch, locationDetectDisabledCookieExpirePeriod],
  );
  const onSetVillage = React.useCallback(
    (villageId: number | undefined) => dispatch(setVillage(villageId)),
    [dispatch],
  );
  const onSetBSCenters = React.useCallback((bsCenterIds: number[]) => dispatch(setBSCenters(bsCenterIds)), [dispatch]);
  const onSelectGeo = React.useCallback((geoValue: TGeoValue) => dispatch(selectGeo(geoValue)), [dispatch]);
  const onGeocode = React.useCallback((query: string): Promise<TGeoValue> => dispatch(geocode({ query })), [dispatch]);
  const onClearGeo = React.useCallback(() => dispatch(clearGeo()), [dispatch]);
  const onSetBuilder = React.useCallback((builderId: number) => dispatch(setBuilder(builderId)), [dispatch]);
  const onSetCoworkingIds = React.useCallback(
    (coworkingIds: number[]) => dispatch(setCoworkingIds(coworkingIds)),
    [dispatch],
  );
  const onChangeSearch = React.useCallback((geoSearch: string) => dispatch(changeGeoSearch(geoSearch)), [dispatch]);
  const onClearGeoError = React.useCallback(() => dispatch(validateGeo(null)), [dispatch]);
  const onGetGeoBbox = React.useCallback(
    (currentRegionOrMaintownId: number) => dispatch(getGeoBbox(currentRegionOrMaintownId)),
    [dispatch],
  );

  return (
    <FilterGeo
      pageKind={pageKind}
      regionOrMaintownId={regionOrMaintownId}
      suggestions={suggestions}
      jsonQuery={jsonQuery}
      placeholder={placeholder}
      isLoading={isLoading}
      search={search}
      geoError={geoError}
      telemetry={telemetry}
      inSuggestTwoStringExperiment={false}
      getGeoBbox={onGetGeoBbox}
      getSuggestions={getSuggestions}
      setRegion={onSetRegion}
      setVillage={onSetVillage}
      setBSCenters={onSetBSCenters}
      selectGeo={onSelectGeo}
      geocode={onGeocode}
      clearGeo={onClearGeo}
      setBuilder={onSetBuilder}
      setCoworkingIds={onSetCoworkingIds}
      changeSearch={onChangeSearch}
      clearGeoError={onClearGeoError}
    />
  );
};
